import { Effect, type Scope } from 'effect';
import { attemptStudio, attemptStudioPromise, type StudioRuntimeError } from '../effect-runtime';
import { trackPdfjsDestroy, type PdfjsDocument } from './pdfjs';
import { openPdfjsDocumentEffect } from './pdfjs-operations';

type Page = Awaited<ReturnType<PdfjsDocument['getPage']>>;
export type PageText = Awaited<ReturnType<Page['getTextContent']>>;

/** Own the loading task and adopted document, joining destruction once on every exit. */
export const withStudioPdfDocumentEffect = Effect.fn('studio.pdfDocument.use')(function* <A>(
  bytes: Uint8Array,
  use: (pdf: PdfjsDocument) => Effect.Effect<A, StudioRuntimeError>,
): Effect.fn.Return<A, StudioRuntimeError, Scope.Scope> {
  const stop = (owner: { pdf?: PdfjsDocument; stopping?: Promise<void> }) =>
    attemptStudioPromise('pdfjs', 'export-document-destroy', () => {
      if (!owner.stopping) {
        owner.stopping = Promise.resolve().then(() => owner.pdf!.destroy());
        trackPdfjsDestroy(owner.stopping);
      }
      return owner.stopping;
    });
  const owner = yield* Effect.acquireRelease(
    Effect.sync(() => ({
      pdf: undefined as PdfjsDocument | undefined,
      stopping: undefined as Promise<void> | undefined,
    })),
    (owner) => (owner.pdf ? stop(owner).pipe(Effect.ignore) : Effect.void),
  );
  yield* openPdfjsDocumentEffect(bytes, (pdf) => {
    owner.pdf = pdf;
  });
  const result = yield* use(owner.pdf!);
  yield* stop(owner);
  return result;
}, Effect.scoped);

/** Project pages while reading so exports retain their original per-page ordering. */
export const collectStudioPageTextEffect = Effect.fn('studio.pdfText.collect')(function* <A>(
  pdf: PdfjsDocument,
  project: (content: PageText, pageNumber: number, pageCount: number) => A,
) {
  const count = yield* attemptStudio('pdfjs', 'export-page-count', () => pdf.numPages);
  const values: A[] = [];
  for (let pageNumber = 1; pageNumber <= count; pageNumber += 1) {
    const page = yield* attemptStudioPromise('pdfjs', 'export-page', () => pdf.getPage(pageNumber));
    const content = yield* attemptStudioPromise('pdfjs', 'export-text', () =>
      page.getTextContent(),
    );
    const value = yield* attemptStudio('pdfjs', 'export-project', () =>
      project(content, pageNumber, count),
    );
    values.push(value);
  }
  return values;
});
